import { prepareNativeDocumentText, releaseNativeDocumentText } from './native-text';
import { walkLayers, type EditorDocument } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { prepareNativeDocumentFonts, releaseNativeDocumentFonts } from './native-fonts';
import { prepareNativeDocumentImages, releaseNativeDocumentImages } from './native-images';

/** Release resources owned by a document, including partially prepared opens. */
export function releaseNativeDocumentResources(document: EditorDocument): void {
  releaseNativeDocumentText(document);
  releaseNativeDocumentFonts(document);
  releaseNativeDocumentImages(document);
}

/** Prepare a newly deserialized candidate before publishing it to a session.
 * This owns failure cleanup; live-document edits use their revision-bound staging APIs.
 */
export async function prepareNativeDocumentResources(document: EditorDocument, signal?: AbortSignal): Promise<void> {
  try {
    // Image preflight enforces encoded/decoded budgets before font preparation
    // verifies the shared retained asset table.
    await prepareNativeDocumentImages(document, signal);
    await prepareNativeDocumentFonts(document, signal);
    await prepareNativeDocumentText(document, signal);
    // Cold recovery and native Open can reach export before Workspace's lazy
    // activation effect finishes. Publish only once these layers can render.
    if (walkLayers(document.root).some(layer => layer.kind === 'vector' || layer.kind === 'text')) {
      const { activateVectorSubsystem } = await import('../vector/runtime');
      throwIfAborted(signal);
      activateVectorSubsystem();
    }
    throwIfAborted(signal);
  } catch (error) {
    releaseNativeDocumentResources(document);
    throw error;
  }
}
